export enum DemographicTarget {
  // IAB Audience Demographics - Age Groups
  AGE_18_24 = "age_18_24",
  AGE_25_34 = "age_25_34",
  AGE_35_44 = "age_35_44",
  AGE_45_54 = "age_45_54",
  AGE_55_64 = "age_55_64",
  AGE_65_PLUS = "age_65_plus",
  
  // IAB Audience Demographics - Gender
  MALE = "male",
  FEMALE = "female",
  
}

// Helper to get display label for DemographicTarget
export const getDemographicLabel = (target: DemographicTarget): string => {
  switch (target) {
    // Age Groups
    case DemographicTarget.AGE_18_24: return "18-24 years";
    case DemographicTarget.AGE_25_34: return "25-34 years";
    case DemographicTarget.AGE_35_44: return "35-44 years";
    case DemographicTarget.AGE_45_54: return "45-54 years";
    case DemographicTarget.AGE_55_64: return "55-64 years";
    case DemographicTarget.AGE_65_PLUS: return "65+ years";
    
    // Gender
    case DemographicTarget.MALE: return "Male";
    case DemographicTarget.FEMALE: return "Female";
    
    
    default: return target;
  }
};

export enum GeographicRegion {
  NORTH_AMERICA = "north_america",
  EUROPE = "europe",
  ASIA_PACIFIC = "asia_pacific",
  LATIN_AMERICA = "latin_america",
  MIDDLE_EAST_AFRICA = "middle_east_africa",
  GLOBAL = "global",
  US_ONLY = "us_only",
  UK_ONLY = "uk_only"
}

export const getGeographicLabel = (region: GeographicRegion): string => {
  switch (region) {
    case GeographicRegion.NORTH_AMERICA: return "North America";
    case GeographicRegion.EUROPE: return "Europe";
    case GeographicRegion.ASIA_PACIFIC: return "Asia Pacific";
    case GeographicRegion.LATIN_AMERICA: return "Latin America";
    case GeographicRegion.MIDDLE_EAST_AFRICA: return "Middle East & Africa";
    case GeographicRegion.GLOBAL: return "Global";
    case GeographicRegion.US_ONLY: return "US Only";
    case GeographicRegion.UK_ONLY: return "UK Only";
    default: return region;
  }
};

export enum ContentGenre {
  COMEDY = "comedy",
  DRAMA = "drama",
  ACTION = "action",
  THRILLER = "thriller",
  DOCUMENTARY = "documentary",
  REALITY = "reality",
  EDUCATIONAL = "educational",
  LIFESTYLE = "lifestyle",
  TECH = "tech",
  GAMING = "gaming",
  MUSIC = "music",
  SPORTS = "sports"
}

export const getContentGenreLabel = (genre: ContentGenre): string => {
  // Capitalize first letter
  return genre.charAt(0).toUpperCase() + genre.slice(1);
};

export enum BrandSafetyLevel {
  STRICT = "strict",
  MODERATE = "moderate",
  RELAXED = "relaxed"
}

export const getBrandSafetyLabel = (level: BrandSafetyLevel): string => {
  switch (level) {
    case BrandSafetyLevel.STRICT: return "Strict (G-Rated only)";
    case BrandSafetyLevel.MODERATE: return "Moderate (PG-Rated OK)";
    case BrandSafetyLevel.RELAXED: return "Relaxed (Some mature themes)";
    default: return level;
  }
};

export enum ContentRating {
  G = "G",
  PG = "PG",
  PG13 = "PG13",
  R = "R"
}

export const getContentRatingLabel = (rating: ContentRating): string => {
  switch (rating) {
    case ContentRating.G: return "G-Rated (General Audiences)";
    case ContentRating.PG: return "PG-Rated (Parental Guidance)";
    case ContentRating.PG13: return "PG-13 (Parents Strongly Cautioned)";
    case ContentRating.R: return "R-Rated (Restricted)";
    default: return rating;
  }
};

export enum TVContentRating {
  TV_Y = "TV-Y",
  TV_Y7 = "TV-Y7",
  TV_G = "TV-G",
  TV_PG = "TV-PG",
  TV_14 = "TV-14",
  TV_MA = "TV-MA"
}

export const getTVContentRatingLabel = (rating: TVContentRating): string => {
  switch (rating) {
    case TVContentRating.TV_Y: return "TV-Y (All Children)";
    case TVContentRating.TV_Y7: return "TV-Y7 (Children 7+)";
    case TVContentRating.TV_G: return "TV-G (General Audience)";
    case TVContentRating.TV_PG: return "TV-PG (Parental Guidance)";
    case TVContentRating.TV_14: return "TV-14 (Parents Strongly Cautioned)";
    case TVContentRating.TV_MA: return "TV-MA (Mature Audiences)";
    default: return rating;
  }
};

// Project to TV Rating mapping based on provided data
export const PROJECT_TV_RATINGS: Record<string, TVContentRating> = {
  "He Came Back": TVContentRating.TV_14,
  "60-SECOND FIRST DATES": TVContentRating.TV_PG,
  "RICH WIDOWS IN PALM SPRINGS": TVContentRating.TV_MA,
  "Casen Pointe: True Crime Detective": TVContentRating.TV_MA,
  "That's So Haven : When your BEST Friend Is Actually A MEAN GIRL": TVContentRating.TV_PG,
  "Detours": TVContentRating.TV_14,
  "American Wind": TVContentRating.TV_MA,
  "David Must Die": TVContentRating.TV_MA,
  "Last To Leave": TVContentRating.TV_MA,
  "THAT THEATRE TEACHER": TVContentRating.TV_PG,
  "Stuck in a Love Triangle with my BFF": TVContentRating.TV_MA,
  "Amica Corporation": TVContentRating.TV_MA,
  "Slowly Cooked": TVContentRating.TV_PG,
  "Injury Law Firms Making a Billboard": TVContentRating.TV_PG,
  "I Love Susu (working title)": TVContentRating.TV_14,
  "Street Therapy": TVContentRating.TV_14,
  "The Algorithm": TVContentRating.TV_14
};

export enum SafetyFilter {
  FAMILY_FRIENDLY = "family_friendly",
  NO_PROFANITY = "no_profanity",
  NO_VIOLENCE = "no_violence",
  NO_CONTROVERSIAL = "no_controversial",
  NO_ALCOHOL_TOBACCO = "no_alcohol_tobacco",
  NO_POLITICAL = "no_political"
}

export const getSafetyFilterLabel = (filter: SafetyFilter): string => {
  switch (filter) {
    case SafetyFilter.FAMILY_FRIENDLY: return "Family-Friendly Content Only";
    case SafetyFilter.NO_PROFANITY: return "No Profanity";
    case SafetyFilter.NO_VIOLENCE: return "No Violence";
    case SafetyFilter.NO_CONTROVERSIAL: return "No Controversial Topics";
    case SafetyFilter.NO_ALCOHOL_TOBACCO: return "No Alcohol/Tobacco";
    case SafetyFilter.NO_POLITICAL: return "No Political Content";
    default: return filter;
  }
};

export enum IntegrationType {
  VISIBLE_LOGO = "visible_logo",
  PRODUCT_USE = "product_use",
  PRODUCT_MENTION = "product_mention",
  CUSTOM_INTEGRATION = "custom_integration"
}

export const getIntegrationTypeLabel = (type: IntegrationType): string => {
  switch (type) {
    case IntegrationType.VISIBLE_LOGO: return "Visible Logo";
    case IntegrationType.PRODUCT_USE: return "Product Use";
    case IntegrationType.PRODUCT_MENTION: return "Product Mention";
    case IntegrationType.CUSTOM_INTEGRATION: return "Custom Integration";
    default: return type;
  }
};

export enum BudgetRange {
  UNDER_10K = "under_10k",
  RANGE_10K_50K = "10k_50k",
  RANGE_50K_100K = "50k_100k",
  RANGE_100K_500K = "100k_500k",
  RANGE_500K_1M = "500k_1m",
  OVER_1M = "over_1m"
}

export const getBudgetRangeLabel = (range: BudgetRange): string => {
  switch (range) {
    case BudgetRange.UNDER_10K: return "Under $10k";
    case BudgetRange.RANGE_10K_50K: return "$10k - $50k";
    case BudgetRange.RANGE_50K_100K: return "$50k - $100k";
    case BudgetRange.RANGE_100K_500K: return "$100k - $500k";
    case BudgetRange.RANGE_500K_1M: return "$500k - $1M";
    case BudgetRange.OVER_1M: return "Over $1M";
    default: return range;
  }
};

export interface BrandPreferencesProfile {
  _id?: string;
  brand_id: string;
  profile_name: string;
  is_default: boolean;
  campaign_id?: string;
  
  // 1. Target Audience
  target_demographics: DemographicTarget[];
  geographic_targets: GeographicRegion[];
  min_audience_size?: number;
  
  // 2. Content Targeting
  target_iab_categories: string[];
  preferred_genres: ContentGenre[];
  excluded_genres: ContentGenre[];
  
  // 3. Brand Safety & Content Ratings
  brand_safety_level: BrandSafetyLevel;
  required_content_rating?: ContentRating;
  safety_filters: SafetyFilter[];
  tv_content_ratings: TVContentRating[]; // New field for TV content ratings
  excluded_content_types: string[];
  
  // 4. Integration Requirements
  required_integration_types: IntegrationType[];
  min_integration_opportunities?: number;
  
  // 5. Budget & Investment
  budget_ranges: BudgetRange[];
  min_investment_amount?: number;
  max_investment_amount?: number;
  
  // 6. Timeline
  campaign_timeline?: string;
  preferred_launch_window?: {
    start: string;
    end: string;
  };
  
  // Metadata
  created_at?: string;
  updated_at?: string;
  is_active?: boolean;
}
